// `observer` is now a no-op here (no observable reads remain after the move off
// DiscoveryUnitsContainerStore). Left in place so this migration doesn't reindent the whole
// component body; safe to drop in a follow-up cleanup.
import {observer, Provider} from 'mobx-react';
import React from 'react';

import {AsyncPriceCourseCardContext} from '@udemy/browse-course';
import {DiscoveryUnit} from '@udemy/discovery-api';
import {FeatureVariantAssignment} from '@udemy/graphql';
import {
    CertificationExamVoucherBannerWrapper,
    CertificationOccupation,
    CertificationOccupationCarousel,
    CertificationTopicTestimonialsSection,
    VoucherUIRegion,
} from '@udemy/react-certification-preparation';
import {ContentArea} from '@udemy/react-layout-components';
import {CONTEXT_TYPES, ResourceContextMenuProvider} from '@udemy/resource-context-menu';
import {useUDData} from '@udemy/ud-data';

import {PersonalPlanBanner} from 'src/components/personal-plan-banner';

import {useTopicRecommendations} from '../hooks/use-topic-recommendations';
import {TopicPageExperimentVariants, TopicProps} from '../types/topic';
import {getPageObjectIdFromTopic} from '../utils';
import {
    AVAILABLE_UNIT_TYPES,
    AVAILABLE_UNIT_VIEW_TYPES,
    isPersonalPlanMerchandisingUnit,
    isTopicSubscriptionUpsellBestsellerUnit,
} from './topic-discovery-units';
import {
    TopicDiscoveryUnitsContainerWrapper,
    TopicDiscoveryUnitsContainerWrapperLoader,
} from './topic-discovery-units-wrapper.react-component';
import {VideoCoursesFallbackCarousel} from './video-courses-fallback-carousel.react-component';

/** Backend recommendation context ("Context Code") for cert topic below-the-fold units. */
export const CERT_TOPIC_DISCOVERY_CONTEXT = 'topic_static_cert_voucher';
/**
 * Page type used for funnel tracking and unit-to-component mapping. Kept as `topic_static`
 * so tracking is unchanged by the move off the REST discovery API; the data itself now comes
 * from the `topic_static_cert_voucher` recommendation context above.
 */
export const CERT_TOPIC_DISCOVERY_PAGE_TYPE = 'topic_static';
/**
 * The REST call asked for `pageSize: 50` units and the context returns 3, so this is a
 * generous ceiling rather than a translation of that number. Item count matches the hook's
 * default and the 12 items per unit the context returns.
 */
const CERT_TOPIC_REQUESTED_SECTION_COUNT = 10;
const CERT_TOPIC_REQUESTED_ITEM_COUNT = 12;
const RESOURCE_CONTEXT_MENU_PROPS = {context: CONTEXT_TYPES.ALL_COURSES};

export const filterCertTopicDiscoveryUnit = (unit: Partial<DiscoveryUnit>) =>
    unit.type !== 'discovery_value_props';

export interface CertificationTopicDiscoverySectionProps
    extends Pick<
            TopicProps,
            'topic' | 'isConsumerSubsSubscriber' | 'isSubsLoading' | 'enrollmentStat'
        >,
        TopicPageExperimentVariants {
    isMobile: boolean;
    pageType: string;
    isTopicVoucherModuleAboveBundle?: FeatureVariantAssignment;
    certificationOccupations?: CertificationOccupation[];
    certificationName?: string;
}

export const CertificationTopicDiscoverySection = observer(
    (props: CertificationTopicDiscoverySectionProps) => {
        const {
            topic,
            enrollmentStat,
            isMobile,
            isConsumerSubsSubscriber,
            isSubsLoading,
            showPersonalPlanBadge,
            useVerticalRecoCourseCardStyle,
            threeCardVerticalLayoutExperimentIsEnabled,
            pageType,
            isTopicVoucherModuleAboveBundle,
            certificationOccupations,
            certificationName,
        } = props;

        const udData = useUDData();
        const isDiscoveryProviderReady = !isSubsLoading && !udData.isGlobalMeContextLoading;
        const showCertificationOccupation = udData.Config.price_country?.id === 'US';

        const mobxProviderProps = {
            isConsumerSubsSubscriber,
            showPersonalPlanBadge: showPersonalPlanBadge ?? false,
            showCodingExercisesBadge: false,
        };

        const verticalRecoCourseCardContextValue =
            useVerticalRecoCourseCardStyle === true
                ? {useCourseProductCard: true, hideCourseDescription: true}
                : {};

        const wrapTopicDiscoveryInContentArea =
            !!isMobile && !!threeCardVerticalLayoutExperimentIsEnabled;

        const pageObjectId = getPageObjectIdFromTopic(topic);

        const {units, isLoading, onLoadMore} = useTopicRecommendations({
            topicId: topic.id,
            pageType: CERT_TOPIC_DISCOVERY_PAGE_TYPE,
            contextId: CERT_TOPIC_DISCOVERY_CONTEXT,
            isEnabled: isDiscoveryProviderReady,
            // This section renders neither related categories nor a bundle carousel.
            shouldFetchContextualHeader: false,
            shouldFetchBundle: false,
            requestedSectionCount: CERT_TOPIC_REQUESTED_SECTION_COUNT,
            requestedItemCountPerUnit: CERT_TOPIC_REQUESTED_ITEM_COUNT,
        });

        if (!isDiscoveryProviderReady || isLoading) {
            return <TopicDiscoveryUnitsContainerWrapperLoader isMobile={!!isMobile} />;
        }

        /**
         * `bestseller` is overloaded on this context. REST told Practice Exams apart by its own
         * `bestseller_practice_test_course` type, but the GraphQL adapter resolves every
         * multi-course unit to `bestseller`, so both it and the Personal Plan upsell have to be
         * excluded by view type. Do not narrow this to `horizontal_tabbed`: Video Courses arrives
         * as `default` on cert topics without skills-hub tabs, and gating on presentation there
         * renders both the real unit and the fallback.
         */
        const videoCoursesUnit = units.find(
            (unit) =>
                unit.type === AVAILABLE_UNIT_TYPES.BESTSELLER &&
                !isTopicSubscriptionUpsellBestsellerUnit(unit) &&
                unit.view_type !== AVAILABLE_UNIT_VIEW_TYPES.CERTIFICATION_PRACTICE_EXAM_CAROUSEL,
        );
        const showVideoCoursesFallback = !videoCoursesUnit?.items?.length;

        const filteredUnits = units.filter(filterCertTopicDiscoveryUnit);
        const ppUpsellIndex = filteredUnits.findIndex(
            (unit) =>
                unit.type === AVAILABLE_UNIT_TYPES.PERSONAL_PLAN_MERCHANDISING ||
                isTopicSubscriptionUpsellBestsellerUnit(unit),
        );

        const personalPlanMerchandisingUnit = units.find(isPersonalPlanMerchandisingUnit);
        const personalPlanModuleHasCourses = (personalPlanMerchandisingUnit?.items ?? []).some(
            (item) => (item as {_class?: string})._class === 'course',
        );

        const isMXUser = !isConsumerSubsSubscriber && !udData.Config.brand.has_organization;
        const showPersonalPlanBannerFallback = isMXUser && !personalPlanModuleHasCourses;
        const examVoucherBannerUiRegion = isTopicVoucherModuleAboveBundle?.configuration
            ?.isTopicVoucherModuleAbovebundle
            ? ('above_course_bundle' as VoucherUIRegion)
            : ('below_course_bundle' as VoucherUIRegion);

        const topicDiscoveryUnits = (
            <TopicDiscoveryUnitsContainerWrapper
                topic={topic}
                enrollmentStat={enrollmentStat}
                isMobile={isMobile}
                units={units as DiscoveryUnit[]}
                onLoadMore={onLoadMore}
                // Section paging comes from the recommendations cursor; leaving infinite scroll
                // on lets DiscoveryUnitsContainer fetch the next page from REST instead.
                disableInfiniteScroll
                pageType={CERT_TOPIC_DISCOVERY_PAGE_TYPE}
                // TODO: hardcoded to 'general' until the cert-specific PP merchandising design in Figma is ready; switch back to 'cert' once it's implemented.
                ppMerchandisingVariant="general"
                discoveryUnitFilter={filterCertTopicDiscoveryUnit}
            >
                <div
                    className="component-margin"
                    data-item-index={ppUpsellIndex !== -1 ? ppUpsellIndex : undefined}
                    key="exam-voucher-banner-and-occupation-carousel"
                >
                    <CertificationExamVoucherBannerWrapper
                        topicId={topic.id}
                        theme="dark"
                        layout="horizontal"
                        isTopicBanner
                        uiRegion={examVoucherBannerUiRegion}
                        ctaMode="buyNow"
                    />
                    {showCertificationOccupation && !!certificationOccupations?.length && (
                        <CertificationOccupationCarousel
                            certificationOccupations={certificationOccupations}
                            certificationName={certificationName ?? ''}
                        />
                    )}
                    {!!topic.testimonials?.length && (
                        <CertificationTopicTestimonialsSection testimonials={topic.testimonials} />
                    )}
                </div>
            </TopicDiscoveryUnitsContainerWrapper>
        );

        return (
            <Provider {...mobxProviderProps}>
                <ResourceContextMenuProvider resourceContextMenuProps={RESOURCE_CONTEXT_MENU_PROPS}>
                    <AsyncPriceCourseCardContext.Provider
                        value={verticalRecoCourseCardContextValue}
                    >
                        {showVideoCoursesFallback && (
                            <VideoCoursesFallbackCarousel
                                topicId={pageObjectId}
                                pageType={pageType}
                            />
                        )}
                        {wrapTopicDiscoveryInContentArea ? (
                            <ContentArea>{topicDiscoveryUnits}</ContentArea>
                        ) : (
                            topicDiscoveryUnits
                        )}
                        {showPersonalPlanBannerFallback && <PersonalPlanBanner />}
                    </AsyncPriceCourseCardContext.Provider>
                </ResourceContextMenuProvider>
            </Provider>
        );
    },
);
